import React, { useState, useMemo } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  VStack,
  HStack,
  Badge,
  Select,
  Spinner,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  Avatar,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { MdRefresh, MdCode } from "react-icons/md";
import { FaTowerBroadcast } from "react-icons/fa6";
import { motion } from "framer-motion";
import Card from "components/card/Card";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyUAdmins } from "utils/accessLevels";

const GlassMorphismCard = styled(Card)`
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;

const MotionTr = motion(Tr);

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => (
  <Button
    size="sm"
    variant={isActive ? "solid" : "outline"}
    colorScheme={isActive ? "orange" : "gray"}
    isDisabled={isDisabled}
    onClick={onClick}
    minW="40px"
    h="40px"
    borderRadius="md"
    _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
    transition="all 0.2s"
  >
    {children}
  </Button>
);

const tariffColumns = [
  { Header: "S.No" },
  { Header: "Station Name" },
  { Header: "Station ID" },
  { Header: "Status" },
  { Header: "DB ID" },
  { Header: "Actions" },
];

const OcpiTariffsTable = ({
  stations,
  refetchStations,
  stListLoading,
  handleBroadcastAll,
  allTarLoading,
  handleBroadcastRow,
  handleViewBody,
  rowBroadcasting,
}) => {
  const [tariffSearch, setTariffSearch] = useState("");
  const [ocpiFilter, setOcpiFilter] = useState("all");

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const tarColumns = useMemo(() => tariffColumns, []);
  const tarData = useMemo(() => {
    if (ocpiFilter === "enabled") {
      return stations.filter((station) => station.IsOcpiEnabled);
    }
    if (ocpiFilter === "disabled") {
      return stations.filter((station) => !station.IsOcpiEnabled);
    }
    return stations;
  }, [stations, ocpiFilter]);

  const tarTableInstance = useTable(
    {
      columns: tarColumns,
      data: tarData,
      initialState: { pageIndex: 0, pageSize: 10 },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") return rows;
        const q = query.toLowerCase().trim();
        return rows.filter((row) => {
          const o = row.original;
          return (
            (o.Name || "").toLowerCase().includes(q) ||
            (o.StationId || "").toLowerCase().includes(q) ||
            String(o.Id ?? "").includes(q)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const handleTarSearchChange = (e) => {
    const v = e.target.value;
    setTariffSearch(v);
    tarTableInstance.setGlobalFilter(v);
  };

  const renderPagination = () => {
    const {
      page,
      state: { pageIndex, pageSize },
      canPreviousPage,
      canNextPage,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize: setPS,
    } = tarTableInstance;

    return (
      <Flex
        justify="space-between"
        align="center"
        px={6}
        py={4}
        borderTop="1px solid"
        borderColor={borderColor}
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={3}>
          <Select
            size="sm"
            value={pageSize}
            onChange={(e) => setPS(Number(e.target.value))}
            borderRadius="md"
            w="120px"
          >
            {[5, 10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500">
            Showing {page.length} of {tarData.length} results
          </Text>
        </HStack>

        <HStack spacing={2}>
          <PaginationButton isDisabled={!canPreviousPage} onClick={() => gotoPage(0)}>
            <ChevronLeftIcon boxSize={4} mr={1} />
            First
          </PaginationButton>
          <PaginationButton isDisabled={!canPreviousPage} onClick={previousPage}>
            <ChevronLeftIcon boxSize={4} />
          </PaginationButton>

          {pageCount <= 5 ? (
            Array.from({ length: pageCount }, (_, i) => (
              <PaginationButton key={i} isActive={i === pageIndex} onClick={() => gotoPage(i)}>
                {i + 1}
              </PaginationButton>
            ))
          ) : (
            <>
              {pageIndex > 1 && (
                <PaginationButton onClick={() => gotoPage(0)}>1</PaginationButton>
              )}
              {pageIndex > 2 && <Text color="gray.400">...</Text>}
              {pageIndex > 0 && (
                <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                  {pageIndex}
                </PaginationButton>
              )}
              <PaginationButton isActive>{pageIndex + 1}</PaginationButton>
              {pageIndex < pageCount - 1 && (
                <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                  {pageIndex + 2}
                </PaginationButton>
              )}
              {pageIndex < pageCount - 3 && <Text color="gray.400">...</Text>}
              {pageIndex < pageCount - 2 && (
                <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                  {pageCount}
                </PaginationButton>
              )}
            </>
          )}

          <PaginationButton isDisabled={!canNextPage} onClick={nextPage}>
            <ChevronRightIcon boxSize={4} />
          </PaginationButton>
          <PaginationButton isDisabled={!canNextPage} onClick={() => gotoPage(pageCount - 1)}>
            Last
            <ChevronRightIcon boxSize={4} ml={1} />
          </PaginationButton>
        </HStack>
      </Flex>
    );
  };

  return (
    <GlassMorphismCard>
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <VStack align="start" spacing={2} width={{ base: "100%", md: "auto" }}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              Search Tariffs
            </Text>
            <HStack spacing={10}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by station name, ID..."
                  value={tariffSearch}
                  onChange={handleTarSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: "#f57d36",
                    boxShadow: "0 0 0 1px #f57d36",
                  }}
                />
              </InputGroup>
              <Select
                size="sm"
                value={ocpiFilter}
                onChange={(e) => setOcpiFilter(e.target.value)}
                borderRadius="md"
                w="170px"
              >
                <option value="all">All OCPI statuses</option>
                <option value="enabled">OCPI Enabled</option>
                <option value="disabled">OCPI Disabled</option>
              </Select>
              <Button
                leftIcon={<Icon as={MdRefresh} />}
                size="sm"
                borderRadius="md"
                variant="ghost"
                color="#f57d36"
                onClick={() => refetchStations()}
                isLoading={stListLoading}
                loadingText="Refreshing..."
                aria-label="Refresh stations"
                fontWeight="500"
              >
                Refresh
              </Button>
              <RoleBasedRender roles={onlyUAdmins}>
                <Button
                  leftIcon={<Icon as={FaTowerBroadcast} boxSize={4} />}
                  onClick={() => handleBroadcastAll("tariffs")}
                  isLoading={allTarLoading}
                  loadingText="Broadcasting..."
                  size="sm"
                  height="36px"
                  minW="160px"
                  px={6}
                  bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
                  color="white"
                  borderRadius="8px"
                  boxShadow="0 4px 12px rgba(245, 125, 54, 0.3)"
                  fontWeight="600"
                  transition="all 0.2s ease"
                  _hover={{
                    bgGradient: "linear(135deg, #f35626 0%, #f57d36 100%)",
                    boxShadow: "0 6px 16px rgba(245, 125, 54, 0.4)",
                    transform: "translateY(-2px)",
                  }}
                  _active={{
                    transform: "translateY(0)",
                    boxShadow: "0 2px 8px rgba(245, 125, 54, 0.3)",
                  }}
                >
                  Broadcast All
                </Button>
              </RoleBasedRender>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
              <Icon as={InfoIcon} boxSize={3} mr={1} />
              Type any keyword to search across all fields
            </Text>
          </VStack>

          <Flex align="center" justify="flex-end">
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Tariffs
            </Text>
            <Badge ml={2} colorScheme="orange" borderRadius="full" px={2}>
              {stations.length} total
            </Badge>
          </Flex>
        </Flex>
      </Box>

      {stListLoading ? (
        <Flex justify="center" py={10}>
          <Spinner color="#f57d36" size="lg" />
        </Flex>
      ) : tarData.length === 0 ? (
        <Flex justify="center" py={10}>
          <Text color="gray.400" fontSize="sm">
            {stations.length === 0
              ? "No stations found."
              : "No stations match the selected OCPI status."}
          </Text>
        </Flex>
      ) : (
        <>
          <TableContainer>
            <Table {...tarTableInstance.getTableProps()} variant="simple" colorScheme="gray">
              <Thead bg={theadBgColor}>
                {tarTableInstance.headerGroups.map((hg, hgIdx) => (
                  <Tr {...hg.getHeaderGroupProps()} key={hgIdx}>
                    {hg.headers.map((column, colIdx) => (
                      <Th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={colIdx}
                        py={4}
                        px={6}
                        borderColor={borderColor}
                        textTransform="uppercase"
                        fontSize="xs"
                        fontWeight="bold"
                        letterSpacing="wider"
                        color="gray.500"
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ color: "#f57d36" }}
                        transition="all 0.2s"
                      >
                        <Flex justify="center" align="center" gap={1}>
                          {column.render("Header")}
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon as={FaSortDown} />
                              ) : (
                                <Icon as={FaSortUp} />
                              )
                            ) : (
                              <Icon as={FaSort} opacity={0.3} />
                            )}
                          </Box>
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...tarTableInstance.getTableBodyProps()}>
                {tarTableInstance.page.map((row, rowIdx) => {
                  tarTableInstance.prepareRow(row);
                  const station = row.original;
                  return (
                    <MotionTr
                      {...row.getRowProps()}
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: rowIdx * 0.03 }}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                    >
                      {row.cells.map((cell, cellIdx) => {
                        let cellData;
                        if (cell.column.Header === "S.No") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm" fontWeight="600">
                                {tarTableInstance.state.pageIndex * tarTableInstance.state.pageSize + rowIdx + 1}
                              </Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Station Name") {
                          const initials = (station.Name || "")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .substring(0, 2);
                          cellData = (
                            <Flex align="center" gap={3}>
                              <Avatar
                                name={station.Name}
                                size="sm"
                                bg="#f57d36"
                                color="white"
                                fontWeight="bold"
                                getInitials={() => initials}
                              />
                              <Box>
                                <Text color={textColor} fontWeight="semibold" fontSize="sm">
                                  {station.Name || "—"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {station.StationId || "—"}
                                </Text>
                              </Box>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Station ID") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm" fontFamily="mono">
                                {station.StationId || "—"}
                              </Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Status") {
                          cellData = (
                            <Flex align="center" justify="center" gap={2} wrap="wrap">
                              <Badge
                                display="flex"
                                alignItems="center"
                                borderRadius="full"
                                px={3}
                                py={1}
                                colorScheme={station.Status === "Available" ? "green" : "gray"}
                                variant="subtle"
                              >
                                <Text fontSize="xs" fontWeight="medium">
                                  {station.Status || "Unknown"}
                                </Text>
                              </Badge>
                              <Tooltip
                                label={
                                  station.IsOcpiEnabled
                                    ? "This station is OCPI-enabled; its tariff can be pushed to peers."
                                    : "OCPI is disabled for this station — tariff pushes/broadcasts are ignored until it's enabled in the Stations tab."
                                }
                                placement="top"
                                hasArrow
                              >
                                <Badge
                                  colorScheme={station.IsOcpiEnabled ? "green" : "red"}
                                  variant="outline"
                                  borderRadius="full"
                                  px={2}
                                >
                                  OCPI {station.IsOcpiEnabled ? "On" : "Off"}
                                </Badge>
                              </Tooltip>
                            </Flex>
                          );
                        } else if (cell.column.Header === "DB ID") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Badge
                                bg="#f57d36"
                                color="white"
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontWeight="bold"
                              >
                                {station.Id}
                              </Badge>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Actions") {
                          cellData = (
                            <Flex align="center" justify="center" gap={2}>
                              <Tooltip label="View OCPI tariff body" placement="top" hasArrow>
                                <IconButton
                                  icon={<Icon as={MdCode} />}
                                  colorScheme="blue"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="View OCPI tariff body"
                                  onClick={() =>
                                    handleViewBody("tariff", station.Id, station.Name || `Station ${station.Id}`)
                                  }
                                />
                              </Tooltip>
                              <RoleBasedRender roles={onlyUAdmins}>
                                <Tooltip
                                  label={
                                    station.IsOcpiEnabled
                                      ? "Broadcast tariff to OCPI peers"
                                      : "Enable OCPI for this station (Stations tab) before broadcasting"
                                  }
                                  placement="top"
                                  hasArrow
                                >
                                  <IconButton
                                    icon={
                                      rowBroadcasting.has(`tariff-${station.Id}`) ? (
                                        <Spinner size="xs" />
                                      ) : (
                                        <Icon as={FaTowerBroadcast} />
                                      )
                                    }
                                    size="sm"
                                    borderRadius="full"
                                    bg="#f57d36"
                                    color="white"
                                    _hover={{
                                      bg: "#e06a2a",
                                      transform: "translateY(-1px)",
                                    }}
                                    aria-label="Broadcast tariff"
                                    isDisabled={
                                      rowBroadcasting.has(`tariff-${station.Id}`) ||
                                      !station.IsOcpiEnabled
                                    }
                                    onClick={() => handleBroadcastRow("tariff", station.Id)}
                                  />
                                </Tooltip>
                              </RoleBasedRender>
                            </Flex>
                          );
                        }
                        return (
                          <Td
                            {...cell.getCellProps()}
                            key={cellIdx}
                            py={4}
                            px={6}
                            borderColor={borderColor}
                            fontSize="sm"
                          >
                            {cellData}
                          </Td>
                        );
                      })}
                    </MotionTr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {renderPagination()}
        </>
      )}
    </GlassMorphismCard>
  );
};

export default OcpiTariffsTable;
